// SPDX-License-Identifier: AGPL-3.0-or-later

// Popout for choosing a channel glyph. "Icons" offers the curated Phosphor set with a stroke
// weight and tint; "Emoji" reuses the app's emoji picker (Unicode + guild custom emoji).

import {
	CHANNEL_GLYPH_COLORS,
	CHANNEL_GLYPH_DEFAULT_COLOR,
	CHANNEL_GLYPH_ICON_GROUPS,
} from '@app/features/channel/glyph/ChannelGlyphCatalog';
import styles from '@app/features/channel/glyph/ChannelGlyphPicker.module.css';
import {ChannelGlyphView} from '@app/features/channel/glyph/ChannelGlyphView';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import {ExpressionPickerPopout} from '@app/features/expressions/components/popouts/ExpressionPickerPopout';
import {
	type ChannelGlyph,
	type ChannelGlyphWeight,
	formatChannelGlyph,
	parseChannelGlyph,
} from '@fluxer/constants/src/ChannelGlyph';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {HashIcon, MagnifyingGlassIcon, SpeakerHighIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {useMemo, useState} from 'react';

const ICONS_TAB_DESCRIPTOR = msg({message: 'Icons', comment: 'Tab in the channel icon picker listing vector icons.'});
const EMOJI_TAB_DESCRIPTOR = msg({message: 'Emoji', comment: 'Tab in the channel icon picker listing emoji.'});
const SEARCH_ICONS_DESCRIPTOR = msg({
	message: 'Search icons',
	comment: 'Placeholder of the search box in the channel icon picker.',
});
const ALL_DESCRIPTOR = msg({message: 'All', comment: 'Channel icon picker category chip showing every icon.'});
const STYLE_DESCRIPTOR = msg({message: 'Style', comment: 'Label above the icon stroke style options in the channel icon picker.'});
const STYLE_THIN_DESCRIPTOR = msg({message: 'Thin', comment: 'Channel icon picker stroke style: regular outline.'});
const STYLE_BOLD_DESCRIPTOR = msg({message: 'Bold', comment: 'Channel icon picker stroke style: thick outline.'});
const STYLE_FILLED_DESCRIPTOR = msg({message: 'Filled', comment: 'Channel icon picker stroke style: solid fill.'});
const COLOR_DESCRIPTOR = msg({message: 'Color', comment: 'Label above the colour swatches in the channel icon picker.'});
const CUSTOM_COLOR_DESCRIPTOR = msg({
	message: 'Custom color',
	comment: 'Accessible label of the free colour input in the channel icon picker.',
});
const NO_ICONS_DESCRIPTOR = msg({
	message: 'No icons match that search',
	comment: 'Empty state of the channel icon picker search.',
});
const REMOVE_DESCRIPTOR = msg({message: 'Remove', comment: 'Button that clears the channel icon.'});
const DONE_DESCRIPTOR = msg({message: 'Done', comment: 'Button that closes the channel icon picker.'});
const PREVIEW_NAME_DESCRIPTOR = msg({
	message: 'channel-name',
	comment: 'Fallback channel name shown in the channel icon picker preview. Lowercase, hyphenated.',
});

const WEIGHTS: ReadonlyArray<{value: ChannelGlyphWeight; label: typeof STYLE_THIN_DESCRIPTOR}> = [
	{value: 'regular', label: STYLE_THIN_DESCRIPTOR},
	{value: 'bold', label: STYLE_BOLD_DESCRIPTOR},
	{value: 'fill', label: STYLE_FILLED_DESCRIPTOR},
];

interface ChannelGlyphPickerProps {
	value: string | null;
	onChange: (value: string | null) => void;
	onClose: () => void;
	channelName?: string;
	isVoice?: boolean;
	/** Lets the emoji tab resolve which custom emoji are usable. */
	channelId?: string;
}

export function ChannelGlyphPicker({value, onChange, onClose, channelName, isVoice, channelId}: ChannelGlyphPickerProps) {
	const {i18n} = useLingui();
	// The popout keeps the props it opened with, so track the selection locally.
	const [selected, setSelected] = useState(value);
	const current = parseChannelGlyph(selected);
	const [tab, setTab] = useState<'icons' | 'emoji'>(current && current.kind !== 'icon' ? 'emoji' : 'icons');
	const [query, setQuery] = useState('');
	const [groupId, setGroupId] = useState<string | null>(null);
	const [weight, setWeight] = useState<ChannelGlyphWeight>(current?.kind === 'icon' ? current.weight : 'fill');
	const [color, setColor] = useState(current?.kind === 'icon' ? current.color : CHANNEL_GLYPH_DEFAULT_COLOR);

	const emit = (glyph: ChannelGlyph | null) => {
		const next = glyph ? formatChannelGlyph(glyph) : null;
		setSelected(next);
		onChange(next);
	};
	const restyle = (next: {weight?: ChannelGlyphWeight; color?: string}) => {
		if (next.weight) setWeight(next.weight);
		if (next.color) setColor(next.color);
		if (current?.kind === 'icon') emit({...current, ...next});
	};

	const groups = useMemo(() => {
		const needle = query.trim().toLocaleLowerCase();
		return CHANNEL_GLYPH_ICON_GROUPS.filter((group) => groupId == null || group.id === groupId)
			.map((group) => ({
				...group,
				icons: needle
					? group.icons.filter((entry) => `${entry.key} ${entry.tags}`.toLocaleLowerCase().includes(needle))
					: group.icons,
			}))
			.filter((group) => group.icons.length > 0);
	}, [query, groupId]);

	const handleEmojiSelect = (emoji: FlatEmoji) => {
		if (emoji.guildId && emoji.id) {
			emit({kind: 'custom', id: emoji.id, animated: emoji.animated === true});
		} else if (emoji.surrogates) {
			emit({kind: 'emoji', emoji: emoji.surrogates});
		} else {
			return;
		}
		onClose();
	};

	const TypeIcon = isVoice ? SpeakerHighIcon : HashIcon;
	return (
		<div className={styles.picker} data-flx="channel.glyph-picker">
			<div className={styles.preview} aria-hidden="true">
				<TypeIcon className={styles.previewType} />
				<ChannelGlyphView glyph={current} animate />
				<span className={styles.previewName}>{channelName?.trim() || i18n._(PREVIEW_NAME_DESCRIPTOR)}</span>
			</div>
			<div className={styles.tabs} role="tablist">
				{(['icons', 'emoji'] as const).map((id) => (
					<button
						key={id}
						type="button"
						role="tab"
						aria-selected={tab === id}
						className={clsx(styles.tab, tab === id && styles.tabActive)}
						onClick={() => setTab(id)}
					>
						{i18n._(id === 'icons' ? ICONS_TAB_DESCRIPTOR : EMOJI_TAB_DESCRIPTOR)}
					</button>
				))}
			</div>
			{tab === 'emoji' ? (
				<div className={styles.emojiHost}>
					<ExpressionPickerPopout channelId={channelId} onEmojiSelect={handleEmojiSelect} visibleTabs={['emojis']} />
				</div>
			) : (
				<>
					<label className={styles.search}>
						<MagnifyingGlassIcon className={styles.searchIcon} aria-hidden="true" />
						<input
							className={styles.searchInput}
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder={i18n._(SEARCH_ICONS_DESCRIPTOR)}
							// biome-ignore lint/a11y/noAutofocus: the picker opens on an explicit click
							autoFocus
						/>
					</label>
					<div className={styles.chips}>
						<button
							type="button"
							className={clsx(styles.chip, groupId == null && styles.chipActive)}
							onClick={() => setGroupId(null)}
						>
							{i18n._(ALL_DESCRIPTOR)}
						</button>
						{CHANNEL_GLYPH_ICON_GROUPS.map((group) => (
							<button
								key={group.id}
								type="button"
								className={clsx(styles.chip, groupId === group.id && styles.chipActive)}
								onClick={() => setGroupId(group.id)}
							>
								{i18n._(group.label)}
							</button>
						))}
					</div>
					<div className={styles.grid} style={{color: `#${color}`}}>
						{groups.length === 0 ? <p className={styles.empty}>{i18n._(NO_ICONS_DESCRIPTOR)}</p> : null}
						{groups.map((group) => (
							<section key={group.id} className={styles.group}>
								{groupId == null ? <h4 className={styles.groupTitle}>{i18n._(group.label)}</h4> : null}
								<div className={styles.cells}>
									{group.icons.map((entry) => {
										const selected = current?.kind === 'icon' && current.key === entry.key;
										return (
											<button
												key={entry.key}
												type="button"
												title={entry.key}
												aria-label={entry.key}
												aria-pressed={selected}
												className={clsx(styles.cell, selected && styles.cellSelected)}
												onClick={() => emit({kind: 'icon', key: entry.key, weight, color})}
											>
												<entry.icon weight={weight} className={styles.cellIcon} />
											</button>
										);
									})}
								</div>
							</section>
						))}
					</div>
					<div className={styles.styleBar}>
						<div className={styles.styleRow}>
							<span className={styles.styleLabel}>{i18n._(STYLE_DESCRIPTOR)}</span>
							<div className={styles.weights}>
								{WEIGHTS.map((option) => (
									<button
										key={option.value}
										type="button"
										aria-pressed={weight === option.value}
										className={clsx(styles.weight, weight === option.value && styles.weightActive)}
										onClick={() => restyle({weight: option.value})}
									>
										{i18n._(option.label)}
									</button>
								))}
							</div>
						</div>
						<div className={styles.styleRow}>
							<span className={styles.styleLabel}>{i18n._(COLOR_DESCRIPTOR)}</span>
							<div className={styles.swatches}>
								{CHANNEL_GLYPH_COLORS.map((swatch) => (
									<button
										key={swatch}
										type="button"
										aria-label={`#${swatch}`}
										aria-pressed={color === swatch}
										className={clsx(styles.swatch, color === swatch && styles.swatchActive)}
										style={{backgroundColor: `#${swatch}`}}
										onClick={() => restyle({color: swatch})}
									/>
								))}
								<input
									type="color"
									className={styles.customColor}
									aria-label={i18n._(CUSTOM_COLOR_DESCRIPTOR)}
									value={`#${color}`}
									onChange={(event) => restyle({color: event.target.value.slice(1).toLowerCase()})}
								/>
							</div>
						</div>
					</div>
				</>
			)}
			<div className={styles.footer}>
				<button type="button" className={styles.removeButton} disabled={!current} onClick={() => emit(null)}>
					{i18n._(REMOVE_DESCRIPTOR)}
				</button>
				<button type="button" className={styles.doneButton} onClick={onClose}>
					{i18n._(DONE_DESCRIPTOR)}
				</button>
			</div>
		</div>
	);
}
